import axios from 'axios';
import { PortfolioPosition } from '../types';

// Every backend call goes through the instance below; no page builds an API URL
// or an auth header. This constant is exported only for what can't use the
// instance (the live-price WebSocket URL).
export const API_BASE_URL = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api/v1`
  : 'http://localhost:8000/api/v1';

// Create axios instance with interceptor for auth token
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

// Add token to all requests. Public endpoints (register, verify-email, blogs,
// Stripe config) have no auth dependency, so a stale token there is ignored.
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  // selectedTier is embedded in the verification email's link
  register: (data: { email: string; password: string; full_name: string }, selectedTier: string) =>
    axiosInstance.post('/auth/register', data, { params: { selected_tier: selectedTier } }),

  login: (data: { email: string; password: string }) =>
    axiosInstance.post('/auth/login', data),

  getCurrentUser: () =>
    axiosInstance.get('/auth/me'),

  // Returns access_token for auto-login
  verifyEmail: (token: string) =>
    axiosInstance.get('/auth/verify-email', { params: { token } }),

  resendVerification: (data: { email: string }, selectedTier?: string) =>
    axiosInstance.post(
      `/auth/resend-verification${selectedTier ? `?selected_tier=${selectedTier}` : ''}`,
      data
    ),

  forgotPassword: (data: { email: string }) =>
    axiosInstance.post('/auth/forgot-password', data),

  resetPassword: (data: { token: string; new_password: string }) =>
    axiosInstance.post('/auth/reset-password', data),

  changePassword: (data: { current_password: string; new_password: string }) =>
    axiosInstance.post('/auth/change-password', data),
};

// Watchlist API
export const watchlistAPI = {
  getAll: () =>
    axiosInstance.get('/watchlist'),
  
  add: (data: { ticker: string; notes?: string; target_price?: number }) =>
    axiosInstance.post('/watchlist', data),
  
  remove: (id: string) =>
    axiosInstance.delete(`/watchlist/${id}`),
  
  update: (id: string, data: { notes?: string; target_price?: number }) =>
    axiosInstance.put(`/watchlist/${id}`, data),
};

// Entity identity verdict — attached to /financials, /dcf/*, /relval/inputs.
//
// A ticker is a mutable label, so a vendor can serve one company's filings
// under another company's symbol. The backend resolves identity through SEC
// EDGAR upstream of the data fetch and returns one verdict, which is the ONLY
// thing any page should gate on. Do not re-derive it per component: doing so is
// what previously let a "wrong entity" warning render beside green
// "actual data" badges vouching for the same numbers.
//
//   match                -> proceed normally, trust badges allowed
//   contradiction        -> hard block: no figures, no pre-filled params, no badges
//   successor_registrant -> NOT a block. The vendor is serving the *predecessor*
//                           registrant of the same business: a reorganization or
//                           change of domicile mints a new CIK without the
//                           company changing, and successors present predecessor
//                           financials. Warn, and note that post-reorganization
//                           filings are missing. (TE: FREYR Battery -> T1 Energy.)
//   cannot_resolve       -> NOT a block. Funds/ETFs are absent from EDGAR's
//                           company file and new registrants lag it.
export type EntityTrustVerdict =
  | 'match'
  | 'contradiction'
  | 'successor_registrant'
  | 'cannot_resolve';

export interface EntityTrust {
  verdict: EntityTrustVerdict;
  edgar_cik: number | null;
  edgar_company_name: string | null;
  fmp_filing_cik: number | null;
  fmp_profile_cik: number | null;
  basis:
    | 'edgar_vs_filing'
    | 'fmp_internal'
    | 'unresolved'
    | 'edgar_name_lineage'
    | 'fund_registrant';
  message: string | null;
  // Registered fund (ETF / ETN / mutual fund) per SEC's fund file. Coverage
  // across SEC's two ticker files is uneven — SPY, GLD, USO and QQQ file as
  // trusts or LPs and appear in the operating file, so they keep the real CIK
  // comparison; ULTY, VOO, JEPI and IWM appear only in the fund file. When this
  // is true and no operating CIK resolved, the vendor-vs-vendor fallback is
  // skipped so an ETF page can't hard-block on a vendor inconsistency.
  is_fund?: boolean;
  fund_series_id?: string | null;
  fund_class_id?: string | null;
  // Present once a contradiction has been classified against EDGAR's own
  // submissions records for both registrants.
  predecessor_name?: string | null;
  predecessor_dormant?: boolean;
  // Positively verified against EDGAR, not inferred from a vendor disagreement.
  edgar_has_filings?: boolean;
  edgar_latest_form?: string | null;
  edgar_latest_filing_date?: string | null;
  edgar_exchanges?: string[];
  edgar_tickers?: string[];
}

// The single test every surface uses. Only a positive contradiction blocks —
// succession explicitly does not, so gates keyed on this proceed normally.
export const isEntityContradicted = (t?: EntityTrust | null): boolean =>
  t?.verdict === 'contradiction';

// The vendor is serving the predecessor registrant's filings: same business,
// figures shown, but anything after the reorganization is absent.
export const isEntitySuccessor = (t?: EntityTrust | null): boolean =>
  t?.verdict === 'successor_registrant';

// Portfolio API
export type TransactionType = 'BUY' | 'SELL' | 'ADJUST' | 'REVERSAL';

export interface SellPositionPayload {
  quantity: number;
  sell_price: number;
  sell_date: string;          // YYYY-MM-DD
  notes?: string;
}

export interface SellPositionResponse {
  transaction_id: string;
  ticker: string;
  quantity_sold: number;
  sell_price: number;
  sell_date: string;
  cost_basis_per_share: number;
  realized_pl: number;
  realized_pl_percent: number;
  proceeds: number;
  remaining_quantity: number;
  position_closed: boolean;
  // Null when the sale closed the position and its holdings row was removed.
  position: PortfolioPosition | null;
}

export interface VoidTransactionResponse {
  reversal_transaction_id: string;
  voided_transaction_id: string;
  ticker: string;
  quantity_restored: number;
  realized_pl_removed: number;
  position_reopened: boolean;
  position: PortfolioPosition | null;
}

export interface PortfolioTransaction {
  id: string;
  ticker: string;
  transaction_type: TransactionType;
  quantity: number;
  price: number;
  transaction_date: string;   // YYYY-MM-DD
  amount: number;
  cost_basis_per_share: number | null;
  realized_pl: number | null;         // null on buys and adjustments
  realized_pl_percent: number | null;
  reverses_transaction_id: string | null;
  is_voided: boolean;
  notes?: string;
  created_at: string;
}

export interface PortfolioTransactionsResponse {
  transactions: PortfolioTransaction[];
  total: number;
  limit: number;
  offset: number;
  /** Every ticker in the ledger, not only those on this page. */
  tickers: string[];
}

export const portfolioAPI = {
  getAll: () =>
    axiosInstance.get('/portfolio'),

  add: (data: { ticker: string; quantity: number; buy_price: number; buy_date: string; notes?: string }) =>
    axiosInstance.post('/portfolio/positions', data),

  remove: (id: string) =>
    axiosInstance.delete(`/portfolio/positions/${id}`),

  update: (id: string, data: { quantity?: number; buy_price?: number; buy_date?: string; notes?: string }) =>
    axiosInstance.put(`/portfolio/positions/${id}`, data),

  sell: (id: string, data: SellPositionPayload) =>
    axiosInstance.post<SellPositionResponse>(`/portfolio/positions/${id}/sell`, data),

  voidTransaction: (transactionId: string) =>
    axiosInstance.post<VoidTransactionResponse>(`/portfolio/transactions/${transactionId}/void`),

  getTransactions: (params?: { ticker?: string; transaction_type?: TransactionType; limit?: number; offset?: number }) =>
    axiosInstance.get<PortfolioTransactionsResponse>('/portfolio/transactions', { params }),

  analyze: () =>
    axiosInstance.post('/portfolio/analyze'),
};

// Alerts API
export const alertsAPI = {
  getAll: () =>
    axiosInstance.get('/alerts/'),
  
  create: (data: { ticker: string; condition: 'above' | 'below'; target_price: number; notes?: string }) =>
    axiosInstance.post('/alerts/', data),
  
  delete: (id: string) =>
    axiosInstance.delete(`/alerts/${id}`),
  
  update: (id: string, data: { is_active?: boolean; notes?: string }) =>
    axiosInstance.patch(`/alerts/${id}`, data),
};

// Technical Alerts API
export const technicalAlertsAPI = {
  getAll: () =>
    axiosInstance.get('/technical-alerts/'),

  create: (data: {
    ticker: string;
    alert_type: string;
    config: Record<string, any>;
    notes?: string;
  }) =>
    axiosInstance.post('/technical-alerts/', data),

  delete: (id: string) =>
    axiosInstance.delete(`/technical-alerts/${id}`),

  update: (id: string, data: { is_active?: boolean; notes?: string }) =>
    axiosInstance.patch(`/technical-alerts/${id}`, data),
};

// Stocks API
export const stocksAPI = {
  getQuote: (ticker: string) =>
    axiosInstance.get(`/stocks/quote/${ticker}`),
  
  getCompany: (ticker: string) =>
    axiosInstance.get(`/stocks/company/${ticker}`),

  // Bulk ticker -> sector lookup. Backed by stock_snapshots, so it agrees with the
  // sector shown in Company Details. Max 250 tickers per call.
  getSectors: (tickers: string[]) =>
    axiosInstance.get<{ sectors: Record<string, string> }>(`/stocks/sectors`, {
      params: { tickers: tickers.join(',') },
    }),

  getHistorical: (ticker: string, days: number = 30) =>
    axiosInstance.get(`/stocks/historical/${ticker}`, { params: { days } }),

  getNews: (ticker: string, limit: number = 3) =>
    axiosInstance.get(`/stocks/news/${ticker}`, { params: { limit }}),

  getMarketNews: (limit: number = 3) =>
    axiosInstance.get(`/stocks/news-market/latest`, { params: { limit }}),

  getTopGainers: (limit: number = 10) =>
    axiosInstance.get(`/stocks/top-gainers`, {params: {limit}}),

  getDailySnapshot: (limit: number = 10, tickers?: string[], asset_type?: string, sector?: string) => {
    const params: any = { limit };
    if (tickers && tickers.length > 0) {
      params.tickers = tickers.join(',');
    }
    if (asset_type) {
      params.asset_type = asset_type;
    }
    // Sector is resolved server-side from stock_snapshots.sector, the same column
    // Company Details reads. Never pass a client-derived sector ticker list here.
    if (sector) {
      params.sector = sector;
    }
    return axiosInstance.get(`/stocks/daily-snapshot`, { params });
  },

  getTopLosers: (limit: number = 10) =>
    axiosInstance.get(`/stocks/top-losers`, { params: { limit } }),

  getIpos: () =>
    axiosInstance.get(`/stocks/ipos`),

  getCommodityQuotes: () =>
    axiosInstance.get(`/stocks/commodity-quotes`),

  getIndexQuotes: () =>
    axiosInstance.get(`/stocks/index-quotes`),

  getCryptoQuotes: () =>
    axiosInstance.get(`/stocks/crypto-quotes`),

  getDividends: (ticker: string) =>
    axiosInstance.get(`/stocks/dividends/${encodeURIComponent(ticker)}`),

  getEtfHoldings: (ticker: string, limit: number = 10) =>
    axiosInstance.get(`/stocks/etf/${encodeURIComponent(ticker)}/holdings`, { params: { limit } }),

  getEtfInfo: (ticker: string) =>
    axiosInstance.get(`/stocks/etf/${encodeURIComponent(ticker)}/info`),

  getOwnership: (ticker: string) =>
    axiosInstance.get(`/stocks/ownership/${ticker}`),

  getImpliedVolatility: (ticker: string, lookback_days: number = 30) =>
    axiosInstance.get(`/stocks/${ticker}/implied-volatility`, { params: { lookback_days } }),

  getTreasuryRates: () =>
    axiosInstance.get(`/stocks/treasury-rates`),

  // Search endpoints
  search: (query: string) =>
    axiosInstance.get(`/stocks/search`, { params: { q: query } }),

  searchByKeywords: (keywords: string, limit: number = 20) =>
    axiosInstance.get(`/stocks/search-by-keywords`, { params: { keywords, limit } }),
};

// DCF API
export const dcfAPI = {
  getSuggestions: (ticker: string) =>
    axiosInstance.get(`/dcf/suggestions/${ticker}`),
  
  calculate: (ticker: string, params: {
    growth_rate: number;
    terminal_growth: number;
    discount_rate: number;
    projection_years: number;
    // Optional user-supplied figures; they override the vendor's. `fcf_override`
    // is REQUIRED when the entity-trust verdict is a contradiction, because free
    // cash flow then has no trustworthy source and it is the input that decides
    // the answer. Omit both for normal tickers.
    fcf_override?: number;
    shares_override?: number;
  }) =>
    axiosInstance.get(`/dcf/calculate/${ticker}`, { params }),
};

// Relative Valuation API
export const relvalAPI = {
  getInputs: (ticker: string) =>
    axiosInstance.get(`/relval/inputs/${ticker}`),

  getPeerRatios: (tickers: string[]) =>
    axiosInstance.post('/relval/peer-ratios', { tickers }),

  calculate: (payload: {
    fwd_eps: number;
    fwd_revenue_b: number;
    fwd_ebitda_b: number;
    net_debt_b: number;
    diluted_shares_m: number;
    current_price?: number | null;
    median_pe: number;
    median_ps: number;
    median_ev_ebitda: number;
    trailing_pe?: number | null;
  }) =>
    axiosInstance.post('/relval/calculate', payload),
};

// Watchlist screening — only the plans with `watchlistScreens` in
// data/pricingTiers.ts, and metered on its own quota because one screen costs a
// price-history fetch per watchlist ticker.
export type WatchlistScreenPreset =
  | 'oversold'
  | 'overbought'
  | 'strong-uptrend'
  | 'reversal-candidates';

export interface WatchlistScreenMatch {
  ticker: string;
  company_name: string;
  current_price: number;
  rsi: number | null;
  macd_trend: string | null;
  above_sma_20: boolean | null;
  above_sma_50: boolean | null;
  bollinger_position: string | null;
  match_reasons: string[];
}

export interface WatchlistScreenResponse {
  matches: WatchlistScreenMatch[];
  total_screened: number;
  matches_found: number;
  filters_applied: string;
  message?: string;
}

// Technical Analysis API
export const technicalAPI = {
  analyze: (ticker: string) =>
    axiosInstance.get(`/technical-analysis/analyze/${ticker}`),
  screenWatchlist: (preset: WatchlistScreenPreset) =>
    axiosInstance.get<WatchlistScreenResponse>(`/technical-analysis/presets/${preset}`),
  aiAnalysis: (ticker: string) =>
    axiosInstance.get(`/stocks/${ticker}/ai-analysis`),
  priceForecast: (ticker: string) =>
    axiosInstance.get(`/stocks/${ticker}/price-forecast`),
};

// Sector Rotation API
export const sectorRotationAPI = {
  getHeatmap: (params?: { window?: string; end_date?: string }) =>
    axiosInstance.get(`/sector-rotation/heatmap`, { params }),

  getTimelapse: (params?: { window?: string; end_date?: string; step_days?: number }) =>
    axiosInstance.get(`/sector-rotation/timelapse`, { params }),

  getCyclePhase: () =>
    axiosInstance.get(`/sector-rotation/cycle-phase`),
};

// Financials API
export const financialsAPI = {
  get: (ticker: string) =>
    axiosInstance.get(`/financials/${ticker}`),
  getAnalystEstimates: (ticker: string) =>
    axiosInstance.get(`/stocks/analyst-estimates/${ticker}`),
  // AI read of the financial statements. Route lives under /stocks/ but this is
  // financials data, so it groups with financialsAPI.get that the panel already calls.
  // `refresh` bypasses the backend's 24h per-ticker cache and always spends a use.
  aiFinancials: (ticker: string, refresh = false) =>
    axiosInstance.get(
      `/stocks/${ticker}/ai-financials`,
      refresh ? { params: { refresh: true } } : undefined
    ),
};

// Stripe / Subscription API
export const stripeAPI = {
  // Publishable key (public endpoint)
  getConfig: () =>
    axiosInstance.get('/stripe/config'),
  
  // refresh: re-read from Stripe first. Use right after a payment, so the plan
  // change shows without waiting for Stripe's webhook.
  getSubscriptionStatus: (refresh = false) =>
    axiosInstance.get('/stripe/subscription-status', refresh ? { params: { refresh: true } } : undefined),
  
  createSubscription: (tier: string) =>
    axiosInstance.post('/stripe/create-subscription', { tier }),

  // Trials: called once the card from create-subscription's SetupIntent is confirmed
  startTrial: (setupIntentId: string) =>
    axiosInstance.post('/stripe/start-trial', { setup_intent_id: setupIntentId }),

  cancelSubscription: () =>
    axiosInstance.post('/stripe/cancel-subscription'),
  
  cancelSubscriptionImmediate: () =>
    axiosInstance.post('/stripe/cancel-subscription-immediate'),

  // Stripe Customer Portal (update card, pay/download invoices) — returns { url }
  openBillingPortal: (returnTo: 'account' | 'billing') =>
    axiosInstance.post('/stripe/billing-portal', { return_to: returnTo }),
};

// One row of /intraday/batch (live quote; the extended-hours fields stay empty
// without FMP batch endpoints)
export interface IntradayQuote {
  ticker: string;
  name: string | null;
  price: number | null;
  previous_close: number | null;
  change: number | null;
  change_percent: number | null;
  market_status: string | null;
  early_trading_change: number | null;
  early_trading_change_percent: number | null;
  late_trading_change: number | null;
  late_trading_change_percent: number | null;
}

// Intraday API
export const intradayAPI = {
  getSnapshot: (ticker: string) =>
    axiosInstance.get(`/intraday/${ticker}`),

  getBarsWithMA: (ticker: string) =>
    axiosInstance.get(`/intraday/${ticker}/bars-with-ma`),

  // The only caller of /intraday/batch (at most 200 tickers). Index the rows
  // with quotesByTicker.
  getBatch: (tickers: string[]) =>
    axiosInstance.get<IntradayQuote[]>(`/intraday/batch`, {
      params: { tickers: tickers.join(',') }
    }),
};

// EXPERIMENTAL, may be removed (see backend services/price_projection.py).
// Percentile bands of a zero-drift GBM Monte Carlo, 252 trading days ahead.
export interface ProjectionPoint {
  date: string;
  p5: number;
  p25: number;
  p50: number;
  p75: number;
  p95: number;
}

export interface PriceProjection {
  ticker: string;
  as_of: string;
  last_price: number;
  method: 'gbm_monte_carlo';
  n_paths: number;
  horizon_days: number;
  percentiles: number[];
  drift_annual: number;
  sigma_annual: number;
  returns_used: 'daily' | 'weekly';
  observations: number;
  zero_return_frac: number;
  median_dollar_volume: number | null;
  confidence: 'normal' | 'reduced' | 'low';
  confidence_notes: string[];
  history: { date: string; close: number }[]; // dividend-adjusted closes
  points: ProjectionPoint[];
}

export const projectionAPI = {
  get: (ticker: string) =>
    axiosInstance.get<PriceProjection>(`/projection/${encodeURIComponent(ticker)}`),
};

// Batch rows keyed by ticker. A row can come back without a price, so callers
// check `price` before using one.
export const quotesByTicker = (rows: unknown): Record<string, IntradayQuote> => {
  const byTicker: Record<string, IntradayQuote> = {};
  if (Array.isArray(rows)) {
    rows.forEach((row: IntradayQuote) => {
      if (row?.ticker) byTicker[row.ticker] = row;
    });
  }
  return byTicker;
};

export const screenerAPI = {
  runScreen: (criteria: object) =>
    axiosInstance.post('/screener/run', criteria),
  getPresets: () =>
    axiosInstance.get('/screener/presets'),
  getFilterOptions: () =>
    axiosInstance.get('/screener/filter-options'),
  // kind: 'screener' = screener filter payload, 'search' = Stock Search keyword query.
  // Both share the saved_screens table and the single saved_screens tier limit.
  getSavedScreens: (kind: 'screener' | 'search' = 'screener') =>
    axiosInstance.get('/screener/saved', { params: { kind } }),
  saveScreen: (data: { name: string; criteria: object; kind?: 'screener' | 'search' }) =>
    axiosInstance.post('/screener/saved', data),
  deleteSavedScreen: (id: string) =>
    axiosInstance.delete(`/screener/saved/${id}`),
};

export const marketAPI = {
  getTickerTape: () => axiosInstance.get('/market/ticker-tape'),
  getScreensTickerTape: () => axiosInstance.get('/market/ticker-tape/screens'),
};

// Blog (public: published posts only)
export const blogAPI = {
  list: () =>
    axiosInstance.get('/content/blogs'),

  getCategories: () =>
    axiosInstance.get('/content/blogs/categories'),

  get: (slug: string) =>
    axiosInstance.get(`/content/blogs/${encodeURIComponent(slug)}`),
};

// Tutorials (published only; needs a live subscription)
export const tutorialsAPI = {
  list: () =>
    axiosInstance.get('/content/tutorials'),

  getCategories: () =>
    axiosInstance.get('/content/tutorials/categories'),
};

// Admin content management (admin accounts only)
export const adminContentAPI = {
  listTutorials: () =>
    axiosInstance.get('/content/admin/tutorials'),

  createTutorial: (data: object) =>
    axiosInstance.post('/content/admin/tutorials', data),

  updateTutorial: (id: string | number, data: object) =>
    axiosInstance.put(`/content/admin/tutorials/${id}`, data),

  deleteTutorial: (id: string | number) =>
    axiosInstance.delete(`/content/admin/tutorials/${id}`),

  listBlogs: () =>
    axiosInstance.get('/content/admin/blogs'),

  createBlog: (data: object) =>
    axiosInstance.post('/content/admin/blogs', data),

  updateBlog: (id: string | number, data: object) =>
    axiosInstance.put(`/content/admin/blogs/${id}`, data),

  deleteBlog: (id: string | number) =>
    axiosInstance.delete(`/content/admin/blogs/${id}`),

  // Claude review of a pasted vendor changelog; the report is stored
  reviewChangelog: (data: { changelog_text: string; vendor: string }) =>
    axiosInstance.post('/content/admin/maintenance/changelog/review', data),

  listMaintenanceReports: () =>
    axiosInstance.get('/content/admin/maintenance/reports'),

  getMaintenanceReport: (id: string) =>
    axiosInstance.get(`/content/admin/maintenance/reports/${id}`),
};